import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useNomenclaturesStore = defineStore("nomenclatures", {
  state: () => ({
    items: [],
    treeNodes: [],
    types: [],
    nomenclatureTypes: [],
    loadingTypes: false,
    loading: false,
    selectedType: null,
    error: null,
    pagination: {
      current_page: 1,
      per_page: 25,
      total: 0
    },
    searchQuery: ""
  }),

  getters: {
    isLoading: (state) => state.loading,
    hasError: (state) => state.error !== null,
    totalPages: (state) =>
      Math.ceil(state.pagination.total / state.pagination.per_page),
    /** Get properties array for a type by slug */
    getTypeProperties: (state) => (typeSlug) => {
      const type = state.types.find((t) => t.value === typeSlug || t.slug === typeSlug)
      return type?.properties ?? []
    },
  },

  actions: {
    async fetchTypes() {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get("/nomenclatures/types")
        const types = response.data?.data ?? response.data ?? []
        this.types = types
        this.nomenclatureTypes = types
        return types
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchNomenclatureTypes() {
      this.loadingTypes = true
      this.error = null
      try {
        const response = await apiV1.get("/nomenclatures/types")
        const types = response.data?.data ?? response.data ?? []
        this.nomenclatureTypes = types
        this.types = types
        return types
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingTypes = false
      }
    },

    async createType(typeData) {
      this.loadingTypes = true
      this.error = null
      try {
        const response = await apiV1.post("/nomenclatures/types", typeData)
        await this.fetchNomenclatureTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingTypes = false
      }
    },

    async updateType(slug, typeData) {
      this.loadingTypes = true
      this.error = null
      try {
        const response = await apiV1.put(
          `/nomenclatures/types/${slug}`,
          typeData
        )
        await this.fetchNomenclatureTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingTypes = false
      }
    },

    async removeType(slug) {
      this.loadingTypes = true
      this.error = null
      try {
        const response = await apiV1.delete(`/nomenclatures/types/${slug}`)
        await this.fetchNomenclatureTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loadingTypes = false
      }
    },

    async fetchByType(type, parentId = null) {
      this.loading = true
      this.error = null
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        }

        if (type) {
          params.type = type
        }

        if (parentId !== null) {
          params.parent_id = parentId
        }

        if (this.searchQuery.trim()) {
          params.search = this.searchQuery.trim()
        }

        const response = await apiV1.get("/nomenclatures/internal", { params })
        const responseData = response.data?.data ?? response.data
        const rawItems = responseData?.data ?? responseData

        if (responseData?.total !== undefined) {
          this.pagination = {
            current_page: responseData.current_page,
            per_page: responseData.per_page,
            total: responseData.total
          }
        }

        this.items = Array.isArray(rawItems) ? rawItems : []
        this.treeNodes = this._buildTreeNodes(this.items)
        return this.items
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchChildren(parentId) {
      this.error = null
      try {
        const params = { parent_id: parentId }
        if (this.selectedType) {
          params.type = this.selectedType
        }
        const response = await apiV1.get("/nomenclatures/internal", { params })
        const responseData = response.data?.data ?? response.data
        return responseData?.data ?? responseData ?? []
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    _buildTreeNodes(items) {
      return items.map((item) => this._itemToNode(item))
    },

    _itemToNode(item) {
      const hasChildren =
        Array.isArray(item.children) && item.children.length > 0
      return {
        key: String(item.id),
        data: item,
        leaf: !hasChildren,
        children: hasChildren
          ? item.children.map((child) => this._itemToNode(child))
          : []
      }
    },

    async create(item) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.post("/nomenclatures/internal", item)
        await this.fetchByType(this.selectedType)
        await this.fetchTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async update(id, item) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.put(
          `/nomenclatures/internal/${id}`,
          item
        )
        await this.fetchByType(this.selectedType)
        await this.fetchTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async remove(id) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.delete(`/nomenclatures/internal/${id}`)
        await this.fetchByType(this.selectedType)
        await this.fetchTypes()
        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    setSelectedType(type) {
      this.selectedType = type
      this.pagination.current_page = 1
    },

    setSearchQuery(query) {
      this.searchQuery = query
      this.pagination.current_page = 1
    },

    setPage(page) {
      this.pagination.current_page = page
    },

    setPerPage(perPage) {
      this.pagination.per_page = perPage
      this.pagination.current_page = 1
    },

    resetFilters() {
      this.selectedType = null
      this.searchQuery = ""
      this.pagination.current_page = 1
    }
  }
})
